/**
 * Service for tracking reputation history over time
 */

interface ReputationEntry {
  year: number;
  month: number;
  day: number;
  value: number;
  timestamp: number;
}

class ReputationHistoryService {
  private static instance: ReputationHistoryService;
  private history: ReputationEntry[] = [];
  private readonly MAX_HISTORY_LENGTH = 365; // Keep up to 1 year of daily data

  private constructor() {
    // Try to load existing history from localStorage
    this.loadHistory();
  }

  static getInstance(): ReputationHistoryService {
    if (!ReputationHistoryService.instance) {
      ReputationHistoryService.instance = new ReputationHistoryService();
    }
    return ReputationHistoryService.instance;
  }

  /**
   * Record a reputation value for a specific game date
   */
  recordReputation(gameDate: { year: number; month: number; day: number }, reputation: number): void {
    // Check if we already have an entry for this date
    const existingIndex = this.history.findIndex(
      entry => entry.year === gameDate.year &&
               entry.month === gameDate.month &&
               entry.day === gameDate.day
    );

    const newEntry: ReputationEntry = {
      year: gameDate.year,
      month: gameDate.month,
      day: gameDate.day,
      value: reputation,
      timestamp: Date.now()
    };

    if (existingIndex >= 0) {
      // Update existing entry
      this.history[existingIndex] = newEntry;
    } else {
      // Add new entry
      this.history.push(newEntry);

      // Sort by date (oldest first)
      this.history.sort((a, b) => {
        const dateA = a.year * 10000 + a.month * 100 + a.day;
        const dateB = b.year * 10000 + b.month * 100 + b.day;
        return dateA - dateB;
      });

      // Trim history if too long
      if (this.history.length > this.MAX_HISTORY_LENGTH) {
        this.history = this.history.slice(-this.MAX_HISTORY_LENGTH);
      }
    }

    // Save to localStorage
    this.saveHistory();
  }

  /**
   * Get monthly reputation averages for the past 12 months
   */
  getMonthlyHistory(currentDate: { year: number; month: number }): { month: string; reputation: number | null }[] {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData: { month: string; reputation: number | null }[] = [];

    for (let i = 0; i < 12; i++) {
      let targetMonth = currentDate.month - i;
      let targetYear = currentDate.year;

      if (targetMonth <= 0) {
        targetMonth += 12;
        targetYear -= 1;
      }

      // Find all entries for this month
      const monthEntries = this.history.filter(
        entry => entry.year === targetYear && entry.month === targetMonth
      );

      let reputation: number | null = null;
      if (monthEntries.length > 0) {
        // Calculate average for the month
        const sum = monthEntries.reduce((acc, entry) => acc + entry.value, 0);
        reputation = sum / monthEntries.length;
      }

      monthlyData.unshift({
        month: months[targetMonth - 1],
        reputation
      });
    }

    // Fill in the current month if not present
    const currentMonthIndex = 11;
    if (monthlyData[currentMonthIndex].reputation === null && this.history.length > 0) {
      // Use the most recent reputation value
      monthlyData[currentMonthIndex].reputation = this.history[this.history.length - 1].value;
    }

    return monthlyData;
  }

  /**
   * Get the full history
   */
  getFullHistory(): ReputationEntry[] {
    return [...this.history];
  }

  /**
   * Clear the history (useful for new games)
   */
  clearHistory(): void {
    this.history = [];
    this.saveHistory();
  }

  /**
   * Save history to localStorage
   */
  private saveHistory(): void {
    try {
      localStorage.setItem('reputationHistory', JSON.stringify(this.history));
    } catch (error) {
      console.error('Failed to save reputation history:', error);
    }
  }

  /**
   * Load history from localStorage
   */
  private loadHistory(): void {
    try {
      const saved = localStorage.getItem('reputationHistory');
      if (saved) {
        this.history = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load reputation history:', error);
      this.history = [];
    }
  }
}

export const reputationHistoryService = ReputationHistoryService.getInstance();